/**
 * Cart page + drawer can both exist with duplicate legacy ids. Section fetches return
 * either AjaxCartForm (drawer) or AjaxCartFormPage (cart template). Sync every cart-form.
 */
function __krownGetCartFormMarkupFromSectionDoc(sectionDoc) {
	const source = sectionDoc.getElementById('AjaxCartFormPage') || sectionDoc.getElementById('AjaxCartForm');
	return source ? source.innerHTML : '';
}

function __krownSyncCartFormsFromSectionDoc(sectionDoc) {
	const html = __krownGetCartFormMarkupFromSectionDoc(sectionDoc);
	if (!html) return;
	const isCartPage = document.body.classList.contains('template-cart');
	if (isCartPage) {
		const pageEl = document.getElementById('AjaxCartFormPage');
		if (pageEl) {
			pageEl.innerHTML = html;
			if (typeof pageEl.ajaxifyCartItems === 'function') {
				pageEl.ajaxifyCartItems();
			}
			// New #cart node after innerHTML swap — re-bind blocking overlay observer.
			__krownInitCartPageBlockingOverlaySync();
		}
	} else {
		document.querySelectorAll('cart-form#AjaxCartForm').forEach((el) => {
			el.innerHTML = html;
			if (typeof el.ajaxifyCartItems === 'function') {
				el.ajaxifyCartItems();
			}
		});
	}
}

function __krownSyncCartSubtotalsFromSectionDoc(sectionDoc) {
	const source = sectionDoc.getElementById('AjaxCartSubtotal');
	if (!source) return;
	const html = source.innerHTML;
	document.querySelectorAll('#AjaxCartSubtotal').forEach((el) => {
		el.innerHTML = html;
	});
}

function __krownSyncHeaderCartFromSectionDoc(sectionDoc) {
	const headerCartCountElm = sectionDoc.querySelector('[data-header-cart-count]');
	if (headerCartCountElm) {
		document.querySelectorAll('[data-header-cart-count]').forEach((elm) => {
			elm.textContent = headerCartCountElm.textContent;
		});
	}
	const headerCartTotalElm = sectionDoc.querySelector('[data-header-cart-total]');
	if (headerCartTotalElm) {
		document.querySelectorAll('[data-header-cart-total]').forEach((elm) => {
			elm.textContent = headerCartTotalElm.textContent;
		});
	}
}

/** Avoid stale Liquid section HTML (browser cache) after cart/remove — phantom totals. */
function __krownHelperCartSectionUrl() {
	return `?section_id=helper-cart&_=${Date.now()}`;
}

function __krownEnsureCartPageBlockingOverlay() {
	if (!document.body.classList.contains('template-cart')) {
		return null;
	}
	let el = document.getElementById('cart-page-sync-overlay');
	if (el) {
		return el;
	}
	const msg =
		(typeof KROWN !== 'undefined' && KROWN.settings?.locales?.cart_updating) ||
		'Updating cart…';
	el = document.createElement('div');
	el.id = 'cart-page-sync-overlay';
	el.className = 'cart-page-sync-overlay';
	el.setAttribute('role', 'status');
	el.setAttribute('aria-live', 'polite');
	el.setAttribute('aria-hidden', 'true');
	el.innerHTML =
		'<div class="cart-page-sync-overlay__backdrop" aria-hidden="true"></div>' +
		'<div class="cart-page-sync-overlay__panel">' +
		'<span class="cart-page-sync-overlay__spinner" aria-hidden="true"></span>' +
		'<span class="cart-page-sync-overlay__text"></span>' +
		'</div>';
	const textEl = el.querySelector('.cart-page-sync-overlay__text');
	if (textEl) {
		textEl.textContent = msg;
	}
	document.body.appendChild(el);
	return el;
}

/**
 * Full-page cart overlay: visible while the cart form is `processing` OR during initial load sync
 * (orphan add-on cleanup + section refresh).
 */
function __krownCartPageOverlaySyncState(form) {
	if (!document.body.classList.contains('template-cart')) {
		return;
	}
	const el = __krownEnsureCartPageBlockingOverlay();
	if (!el) {
		return;
	}
	const busyForm = (form && form.isConnected ? form : null) || document.querySelector('form#cart.cart__form');
	const formProcessing = !!(busyForm && busyForm.classList.contains('processing'));
	const initialSync = document.body.classList.contains('cart-page--initial-sync');
	const show = formProcessing || initialSync;

	if (show) {
		el.classList.add('cart-page-sync-overlay--visible');
		el.setAttribute('aria-hidden', 'false');
		document.body.classList.add('cart-page--blocking');
	} else {
		el.classList.remove('cart-page-sync-overlay--visible');
		el.setAttribute('aria-hidden', 'true');
		document.body.classList.remove('cart-page--blocking');
	}

	void __krownSyncCartPageCheckoutZone();
}

/**
 * Cart page: show checkout wrapper only when cart still has at least one non–add-on line; disable buttons while syncing.
 */
async function __krownSyncCartPageCheckoutZone(prefetchedCart = null) {
	if (!document.body.classList.contains('template-cart')) {
		return;
	}
	const wrap = document.querySelector('[data-js-cart-checkout-actions]');
	const buttons = document.querySelectorAll(
		'#CheckOut, button[data-js-cart-page-checkout], button[name="checkout"][form="cart"]'
	);

	const form = document.querySelector('form#cart.cart__form');
	const blocking =
		document.body.classList.contains('cart-page--initial-sync') ||
		!!(form && form.classList.contains('processing'));

	let cart = prefetchedCart && typeof prefetchedCart === 'object' ? prefetchedCart : null;

	try {
		if (!cart || !Array.isArray(cart.items)) {
			const r = await fetch(`${KROWN.settings.routes.cart_url}.js`, {
				headers: { Accept: 'application/json' },
				cache: 'no-store',
			});
			if (!r.ok) {
				throw new Error('Cart fetch failed');
			}
			cart = await r.json();
		}
	} catch (e) {
		if (wrap) {
			wrap.hidden = false;
			wrap.setAttribute('aria-hidden', 'false');
		}
		buttons.forEach((btn) => {
			btn.disabled = false;
			btn.removeAttribute('aria-disabled');
		});
		return;
	}

	const hasParentLines = __krownCartJsHasParentLines(cart);
	const count = Number(cart.item_count);
	const hasAnyItems = Number.isFinite(count)
		? count > 0
		: (Array.isArray(cart.items) && cart.items.length > 0);
	const itemCount = Number.isFinite(count)
		? count
		: (Array.isArray(cart.items) ? cart.items.length : 0);

	const section = document.querySelector('.cart-section');
	if (section) {
		section.classList.toggle('cart-section--empty', itemCount === 0);
	}

	if (wrap) {
		wrap.hidden = !hasParentLines;
		wrap.setAttribute('aria-hidden', !hasParentLines ? 'true' : 'false');
	}

	buttons.forEach((btn) => {
		btn.disabled = blocking || !hasAnyItems;
		btn.setAttribute('aria-disabled', btn.disabled ? 'true' : 'false');
	});
}

function __krownCartPageSetOverlayLabel(useLoadingWording) {
	const el = document.getElementById('cart-page-sync-overlay');
	if (!el || typeof KROWN === 'undefined') return;
	const textEl = el.querySelector('.cart-page-sync-overlay__text');
	if (!textEl) return;
	const loading = KROWN.settings?.locales?.cart_loading;
	const updating = KROWN.settings?.locales?.cart_updating;
	textEl.textContent = useLoadingWording && loading ? loading : (updating || textEl.textContent);
}

function __krownInitCartPageBlockingOverlaySync() {
	if (!document.body.classList.contains('template-cart')) {
		return;
	}
	const form = document.querySelector('form#cart.cart__form');
	if (!form) {
		return;
	}
	if (window.__krownCartBlockingObserver) {
		window.__krownCartBlockingObserver.disconnect();
		window.__krownCartBlockingObserver = null;
	}
	const apply = () => {
		__krownCartPageOverlaySyncState(form);
	};
	window.__krownCartBlockingObserver = new MutationObserver(apply);
	window.__krownCartBlockingObserver.observe(form, { attributes: true, attributeFilter: ['class'] });
	apply();
}

document.addEventListener('DOMContentLoaded', () => {
	__krownInitCartPageBlockingOverlaySync();
});

/** Serialize orphan cleanups — overlapping calls used to return early and leave stale add-ons for seconds. */
let __krownOrphanCleanupChain = Promise.resolve();

function __krownAddonProps(raw) {
	const p = raw && typeof raw === 'object' ? raw : {};
	return {
		addonParentVariantId: String(p._addon_for_variant_id || p['Addon For Variant ID'] || '').trim(),
		addonGroupId: String(p._addon_group_id || '').trim(),
	};
}

function __krownCartJsItemIsAddon(item) {
	const props = item?.properties || {};
	return !!__krownAddonProps(props).addonParentVariantId;
}

function __krownCartJsHasParentLines(cart) {
	const items = Array.isArray(cart?.items) ? cart.items : [];
	return items.some((line) => !__krownCartJsItemIsAddon(line));
}

/** Cart page: hide checkout block immediately when removing a parent line (not add-on-only removal). */
function __krownSetCartCheckoutActionsHidden(hidden) {
	if (!document.body.classList.contains('template-cart')) {
		return;
	}
	document.querySelectorAll('[data-js-cart-checkout-actions]').forEach((el) => {
		el.hidden = !!hidden;
		el.setAttribute('aria-hidden', hidden ? 'true' : 'false');
	});
}

function __krownBuildOrphanAddonUpdatesFromItems(items) {
	const parentGroupIds = new Set();
	const parentVariantIds = new Set();

	items.forEach((item) => {
		const props = item.properties || {};
		const { addonParentVariantId } = __krownAddonProps(props);
		const isAddon = !!addonParentVariantId;
		if (!isAddon) {
			const groupId = String(props._addon_group_id || '').trim();
			if (groupId) parentGroupIds.add(groupId);
			if (item.variant_id != null) parentVariantIds.add(String(item.variant_id));
		}
	});

	const updates = {};
	items.forEach((item) => {
		const props = item.properties || {};
		const { addonParentVariantId, addonGroupId } = __krownAddonProps(props);
		if (!addonParentVariantId) return;

		const hasParentByGroup = addonGroupId ? parentGroupIds.has(addonGroupId) : false;
		const hasParentByVariant = parentVariantIds.has(addonParentVariantId);
		const hasParent = hasParentByGroup || hasParentByVariant;

		if (!hasParent && item.key) {
			updates[item.key] = 0;
		}
	});
	return updates;
}

/**
 * Remove add-on lines whose parent no longer exists. Uses optional cart JSON from a prior
 * /cart/update.js response to skip a round trip.
 *
 * @param {object|null} prefetchedCart Cart object from update/add response, or null to fetch.
 * @returns {Promise<boolean>} true if cart was modified
 */
function __krownRemoveOrphanAddonLines(prefetchedCart = null) {
	const next = __krownOrphanCleanupChain.then(() => __krownRemoveOrphanAddonLinesImpl(prefetchedCart));
	__krownOrphanCleanupChain = next.catch(() => {
	});
	return next;
}

async function __krownRemoveOrphanAddonLinesImpl(prefetchedCart) {
	let cart = prefetchedCart && typeof prefetchedCart === 'object' ? prefetchedCart : null;
	let modified = false;

	for (let pass = 0; pass < 4; pass++) {
		if (!cart || !Array.isArray(cart.items)) {
			const response = await fetch(`${KROWN.settings.routes.cart_url}.js`, {
				headers: { Accept: 'application/json' },
				cache: 'no-store',
			});
			if (!response.ok) break;
			cart = await response.json();
		}

		const items = Array.isArray(cart.items) ? cart.items : [];
		if (!items.length) break;

		const updates = __krownBuildOrphanAddonUpdatesFromItems(items);
		const keys = Object.keys(updates);
		if (keys.length === 0) break;

		const updRes = await fetch(KROWN.settings.routes.cart_update_url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify({ updates }),
		});
		modified = true;
		cart = null;
		try {
			cart = await updRes.json();
		} catch (_) {
			cart = null;
		}
	}

	return modified;
}

async function __krownFindVariantIdBySku(sku) {
	if (!sku) return null;
	window.__krownAddonSkuVariantCache = window.__krownAddonSkuVariantCache || {};
	if (window.__krownAddonSkuVariantCache[sku]) {
		return window.__krownAddonSkuVariantCache[sku];
	}

	// Fast path: resolve from current cart items first.
	// This is more reliable when products.json search is stale or SKU is reused.
	try {
		const cartResp = await fetch(`${KROWN.settings.routes.cart_url}.js`, {
			headers: { 'Accept': 'application/json' }
		});
		if (cartResp.ok) {
			const cart = await cartResp.json();
			const items = Array.isArray(cart?.items) ? cart.items : [];
			for (const item of items) {
				const props = item?.properties || {};
				const itemSku = (item?.sku || '').trim();
				const addonSku = (props?._addon_sku || props?.['Addon SKU'] || '').trim();
				if ((itemSku && itemSku === sku) || (addonSku && addonSku === sku)) {
					if (item?.variant_id) {
						window.__krownAddonSkuVariantCache[sku] = String(item.variant_id);
						return window.__krownAddonSkuVariantCache[sku];
					}
				}
			}
		}
	} catch (e) {
		// Ignore and continue with products.json lookup.
	}

	let page = 1;
	const perPage = 250;
	const maxPages = 20;
	while (page <= maxPages) {
		const response = await fetch(`${window.Shopify.routes.root}products.json?limit=${perPage}&page=${page}`, {
			headers: { 'Accept': 'application/json' }
		});
		if (!response.ok) break;
		const payload = await response.json();
		const products = Array.isArray(payload?.products) ? payload.products : [];
		if (!products.length) break;
		for (const product of products) {
			const variants = Array.isArray(product?.variants) ? product.variants : [];
			const match = variants.find((variant) => (variant?.sku || '').trim() === sku);
			if (match?.id) {
				window.__krownAddonSkuVariantCache[sku] = String(match.id);
				return window.__krownAddonSkuVariantCache[sku];
			}
		}
		page += 1;
	}
	return null;
}

function __krownBindAddonQuickAddDelegated() {
	if (window.__krownAddonQuickAddDelegatedBound) return;
	window.__krownAddonQuickAddDelegatedBound = true;

	document.addEventListener('click', async (e) => {
		const btn = e.target?.closest?.('.js-addon-quick-add');
		if (!btn) return;

		e.preventDefault();
		e.stopPropagation();
		if (typeof e.stopImmediatePropagation === 'function') {
			e.stopImmediatePropagation();
		}

		if (btn.dataset.quickAddBusy === '1') return;
		btn.dataset.quickAddBusy = '1';

		const row = btn.closest('[data-js-addon-option]');
		if (!row) {
			btn.dataset.quickAddBusy = '0';
			return;
		}

		const cartForm = btn.closest('cart-form');
		const formEl = cartForm?.querySelector('form');
		if (formEl) formEl.classList.add('processing');
		btn.disabled = true;

		try {
			const addonSku = row.dataset.addonSku || '';
			const addonLabel = row.dataset.addonLabel || 'Add-on';
			const parentVariantId = row.dataset.addonParentVariantId || '';
			const addonGroupId = row.dataset.addonGroupId || '';
			const parentTitle = row.dataset.addonParentTitle || '';
			if (!addonSku || !parentVariantId) {
				throw new Error('Missing add-on row metadata');
			}

			const variantId = await __krownFindVariantIdBySku(addonSku);
			if (!variantId) {
				throw new Error(`Could not resolve add-on SKU ${addonSku}`);
			}

			const properties = {
				'_addon_for_variant_id': parentVariantId,
				'_addon_label': addonLabel,
				'_addon_sku': addonSku,
				'_addon_group_id': addonGroupId,
				'_addon_parent_title': parentTitle,
				'Addon Note': addonLabel
			};

			const addResp = await fetch(KROWN.settings.routes.cart_add_url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
				body: JSON.stringify({
					items: [{ id: variantId, quantity: 1, properties }]
				})
			});
			if (!addResp.ok) {
				let message = 'Could not add add-on to cart';
				try {
					const err = await addResp.json();
					message = err?.description || err?.message || message;
				} catch (parseErr) {}
				throw new Error(message);
			}

			const rc = typeof window.refreshCart === 'function' ? window.refreshCart() : null;
			if (rc && typeof rc.then === 'function') {
				await rc;
			} else if (typeof window.refreshCart !== 'function') {
				window.location.reload();
			}
		} catch (err) {
			console.log('Delegated add-on quick add error:', err);
		} finally {
			btn.disabled = false;
			btn.dataset.quickAddBusy = '0';
			if (formEl) formEl.classList.remove('processing');
		}
	}, true);
}

if ( typeof CartForm !== 'function' ) {
	class CartForm extends HTMLElement {

		constructor(){
			super();
			__krownBindAddonQuickAddDelegated();
			this.ajaxifyCartItems();
			const cartFormRoot = this;
			// Full-page loading on cart template until orphan add-ons are reconciled + UI refreshed.
			if (document.body.classList.contains('template-cart')) {
				document.body.classList.add('cart-page--initial-sync');
				__krownCartPageSetOverlayLabel(true);
				__krownCartPageOverlaySyncState(cartFormRoot.form);
				let safetyReleased = false;
				const releaseInitialSync = () => {
					if (safetyReleased) return;
					safetyReleased = true;
					document.body.classList.remove('cart-page--initial-sync');
					__krownCartPageSetOverlayLabel(false);
					__krownCartPageOverlaySyncState(cartFormRoot.querySelector('form#cart.cart__form'));
				};
				const safetyTimer = setTimeout(releaseInitialSync, 10000);
				setTimeout(() => {
					__krownRemoveOrphanAddonLines()
						.then(async (removed) => {
							if (removed && typeof window.refreshCart === 'function') {
								await window.refreshCart();
							}
						})
						.catch((e) => {
							console.log('Initial cart orphan cleanup error:', e);
						})
						.finally(() => {
							clearTimeout(safetyTimer);
							releaseInitialSync();
						});
				}, 0);
			} else {
				setTimeout(() => {
					__krownRemoveOrphanAddonLines().then(async (removed) => {
						if (removed && typeof window.refreshCart === 'function') {
							await window.refreshCart();
						}
					});
				}, 0);
			}
		}

		ajaxifyCartItems(){

			this.form = this.querySelector('form');

			this.querySelectorAll('[data-js-cart-item]').forEach(item=>{

				const remove = item.querySelector('.remove');
				if ( remove ) {
					remove.dataset.href = remove.getAttribute('href');
					remove.setAttribute('href', '');
					remove.addEventListener('click', (e)=>{
						e.preventDefault();
						this.updateCartQty(item, 0);
					})
				}

				const qty = item.querySelector('.qty');
				if ( qty ) {// Prevent quantity changes for freebie items
					if (item.classList.contains('cart-item--freebie')) {
						qty.disabled = true;
						qty.value = 1;
					} else {
						qty.addEventListener('input', debounce(e=>{
							e.preventDefault();
							e.target.blur();
							this.updateCartQty(item, parseInt(qty.value));
						}, 500));
						qty.addEventListener('click', (e)=>{
							e.target.select();
						})
					}
				}

			})

		}

		updateCartQty(item, newQty){

			let alert = null;

			// Prevent quantity changes for freebie items - keep at 1
			if (item.classList.contains('cart-item--freebie')) {
				if (newQty !== 1 && newQty !== 0) {
					// If trying to change freebie quantity (not remove), prevent it
					const qtyInput = item.querySelector('.qty');
					if (qtyInput) {
						qtyInput.value = 1;
					}
					return; // Don't update cart
				}
			}

			const isAddonItem = !!item.dataset.addonParentVariantId;
			if (document.body.classList.contains('template-cart') && newQty === 0 && item.dataset.jsCartItem && !isAddonItem) {
				__krownSetCartCheckoutActionsHidden(true);
			}

			this.form.classList.add('processing');
			if ( this.querySelector('.alert') ) {
				this.querySelector('.alert').remove();
			}

			// If removing a parent product with add-ons, remove add-ons first then parent.
			// Always resolve from fresh cart JSON to avoid stale/merged UI line numbers.
			// Do not run this flow for add-on rows themselves.
			if (newQty === 0 && item.dataset.jsCartItem && !isAddonItem) {
				const parentVariantId = item.dataset.variantId;
				const parentAddonGroupId = String(item.dataset.addonGroupId || '').trim();
				const parentKey = item.dataset.id;
				const cartFormEl = this;

				(async () => {
					try {
						const cartRes = await fetch(`${KROWN.settings.routes.cart_url}.js`, {
							headers: { Accept: 'application/json' },
							cache: 'no-store',
						});
						const cart = await cartRes.json();
						const items = Array.isArray(cart.items) ? cart.items : [];
						const matchedAddonKeys = new Set();

						items.forEach((cartItem) => {
							const props = cartItem.properties || {};
							const { addonParentVariantId, addonGroupId } = __krownAddonProps(props);
							if (!addonParentVariantId || !cartItem.key) return;

							if (parentAddonGroupId) {
								if (addonGroupId === parentAddonGroupId) {
									matchedAddonKeys.add(cartItem.key);
								} else if (!addonGroupId && String(addonParentVariantId) === String(parentVariantId)) {
									// Add-on row missing group id but tied to this parent's variant
									matchedAddonKeys.add(cartItem.key);
								}
							} else if (String(addonParentVariantId) === String(parentVariantId)) {
								matchedAddonKeys.add(cartItem.key);
							}
						});

						const updates = {};
						if (parentKey) {
							updates[parentKey] = 0;
						}
						matchedAddonKeys.forEach((key) => {
							updates[key] = 0;
						});

						const updRes = await fetch(KROWN.settings.routes.cart_update_url, {
							method: 'POST',
							headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
							body: JSON.stringify({ updates }),
						});

						let cartAfter = null;
						try {
							cartAfter = await updRes.json();
						} catch (_) {
							cartAfter = null;
						}

						// Use response cart first (no extra GET), then retry passes if any orphans remain
						await __krownRemoveOrphanAddonLines(cartAfter);
						if (typeof window.refreshCart === 'function') {
							await window.refreshCart();
						}
					} catch (e) {
						console.log('Parent removal error:', e);
						await __krownRemoveOrphanAddonLines(null);
						if (typeof window.refreshCart === 'function') {
							await window.refreshCart();
						}
					} finally {
						const f = cartFormEl.querySelector('form#cart.cart__form');
						if (f) {
							f.classList.remove('processing');
						}
					}
				})();
				return;
			}

			// Standard cart update (no add-ons to remove)
			// Use item key updates to avoid stale line-number mismatches after dynamic cart re-render.
			const itemKey = item.dataset.id;
			const body = itemKey
				? JSON.stringify({ updates: { [itemKey]: newQty } })
				: JSON.stringify({
					line: parseInt(item.dataset.line),
					quantity: newQty
				});
			const updateUrl = itemKey ? KROWN.settings.routes.cart_update_url : KROWN.settings.routes.cart_change_url;

			fetch(updateUrl, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
					body
				})
				.then(response => response.json())
				.then(response => {
					if ( response.status == 422 ) {
						// wrong stock logic alert
						alert = document.createElement('span');
						alert.classList.add('alert', 'alert--error');
						if ( typeof response.description === 'string' ) {
							alert.innerHTML = response.description;
						} else {
							alert.innerHTML = response.message;
						}
					}
					return fetch(__krownHelperCartSectionUrl(), { cache: 'no-store' });
				})
				.then(response => response.text())
				.then(text => {

					const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
					__krownSyncCartFormsFromSectionDoc(sectionInnerHTML);
					__krownSyncCartSubtotalsFromSectionDoc(sectionInnerHTML);

					__krownSyncHeaderCartFromSectionDoc(sectionInnerHTML);

					if ( alert !== null ) {
						this.form.prepend(alert);
					}

					const event = new Event('cart-updated');
					this.dispatchEvent(event);

				})
				.catch(e => {
					console.log(e);
					let alert = document.createElement('span');
					alert.classList.add('alert', 'alert--error');
					alert.textContent = KROWN.settings.locales.cart_general_error;
					this.form.prepend(alert);
				})
				.finally(() => {
					this.form.classList.remove('processing');
				});
		}

	} 


  if ( typeof customElements.get('cart-form') == 'undefined' ) {
		customElements.define('cart-form', CartForm);
	}

}

if ( typeof CartProductQuantity !== 'function' ) {

	class CartProductQuantity extends HTMLElement {
		constructor(){
			super();
			this.querySelector('.qty-minus').addEventListener('click', this.changeCartInput.bind(this));
			this.querySelector('.qty-plus').addEventListener('click', this.changeCartInput.bind(this));
		}
		changeCartInput(e){
			if (e && typeof e.preventDefault === 'function') {
				e.preventDefault();
			}
			const cartItem = this.closest('[data-js-cart-item]');
			// Empty inline add-on rows (quick-add placeholders) are not cart line items.
			// Let their dedicated quick-add handler run without trying to update cart qty here.
			if (!cartItem) {
				return;
			}
			// Prevent quantity changes for freebie items
			if (cartItem.classList.contains('cart-item--freebie')) {
				const qty = this.querySelector('.qty');
				if (qty) {
					qty.value = 1;
				}
				return;
			}
			setTimeout(()=>{
				const cartForm = cartItem.closest('cart-form');
				if (cartForm && typeof cartForm.updateCartQty === 'function') {
					cartForm.updateCartQty(cartItem, parseInt(this.querySelector('.qty').value, 10));
				}
			}, 50);
		}
	}

  if ( typeof customElements.get('cart-product-quantity') == 'undefined' ) {
		customElements.define('cart-product-quantity', CartProductQuantity);
	}

}

// method for apps to tap into and refresh the cart

if ( ! window.refreshCart ) {

	window.refreshCart = () => {
		return fetch(__krownHelperCartSectionUrl(), { cache: 'no-store' })
			.then(response => response.text())
			.then(text => {

				const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
				__krownSyncCartFormsFromSectionDoc(sectionInnerHTML);
				__krownSyncCartSubtotalsFromSectionDoc(sectionInnerHTML);

				// Re-initialize freebie buttons after ajax refresh (defined in cart-form-page)
				if ( typeof window.initFreebieHandlers === 'function' ) {
					setTimeout(() => window.initFreebieHandlers(), 50);
				}

				__krownSyncHeaderCartFromSectionDoc(sectionInnerHTML);

				if ( document.querySelector('cart-recommendations') ) {
					document.querySelector('cart-recommendations').innerHTML = '';
					document.querySelector('cart-recommendations').generateRecommendations();
				}

			})
			.finally(() => {
				void __krownSyncCartPageCheckoutZone();
				if (document.body.classList.contains('template-cart')) {
					const cf = document.getElementById('AjaxCartFormPage');
					if (cf) {
						cf.dispatchEvent(new Event('cart-updated'));
					}
				}
			});

	};

}

if ( ! customElements.get('cart-note') ) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'input',
          debounce(event => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${KROWN.settings.routes.cart_update_url}`, { 
							method: 'POST',
							headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
							body 
						});
		  		}, 150)
				);
      }
    }
  );
}
// Wine Gift Option - Updates cart attributes
if ( ! customElements.get('wine-gift-option') ) {
  customElements.define(
    'wine-gift-option',
    class WineGiftOption extends HTMLElement {
      constructor() {
        super();
        const checkbox = this.querySelector('input[type="checkbox"]');
        if (checkbox) {
          checkbox.addEventListener('change', (event) => {
            const attributes = {
              "Gift Option": event.target.checked ? "Yes" : ""
            };
            const body = JSON.stringify({ attributes: attributes });
            fetch(`${KROWN.settings.routes.cart_update_url}`, { 
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
              body 
            });
          });
        }
      }
    }
  );
}
if ( ! customElements.get('cart-discounts' ) ) {
	customElements.define(
		'cart-discounts',
		class CartDiscounts extends HTMLElement {

			constructor() {
				super();

				const button = this.querySelector('[data-js-discount-submit]');
				this.input = this.querySelector('[data-js-discount-input]');
				this.cartSubtotal = document.getElementById('AjaxCartSubtotal');
				this.cartDiscountError = this.querySelector('[data-js-discount-error]');
				this.discountCodes = document.querySelector('[data-js-discount-codes]');

				this.loadDiscountButtons();

				button.addEventListener('click', (e) => {
					e.preventDefault();
					if ( this.input.value.length > 0 ) {

						this.cartSubtotal.classList.add('processing');
						this.discountCodes.classList.add('processing');

						const existingDiscounts = Array.from(this.querySelectorAll('[data-js-discount]')).map(elm => elm.dataset.jsDiscount);
						
						const allDiscounts = [...existingDiscounts, this.input.value];
						const discountString = allDiscounts.join(',');
						const body = JSON.stringify({ discount: discountString });

						fetch(`${KROWN.settings.routes.cart_update_url}`, { 
							method: 'POST',
							headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
							body 
						})
							.then(response => response.json())
							.then(response => {
								const discountCodes = response.discount_codes;
								const inputValue = this.input.value;
								const matchingCode = discountCodes.find(code => 
									code.code === inputValue
								);

								if (matchingCode) {
									if (matchingCode.applicable) {
										this.handleSuccess();
									} else {
										this.handleError();
									}
								} else {
									this.handleError();
								}
							})
							.catch(error => {
								this.handleError();
								console.log(`Error: ${error}`);
							})

					}

				});

			}

			handleSuccess(){
				fetch(__krownHelperCartSectionUrl(), { cache: 'no-store' })
					.then(response => response.text())
					.then(text => {
						const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
						__krownSyncCartSubtotalsFromSectionDoc(sectionInnerHTML);
						if (this.cartSubtotal) {
							this.cartSubtotal.classList.remove('processing');
						}
						this.cartDiscountError.innerHTML = '';
						if ( sectionInnerHTML.querySelector('[data-js-discount-codes]') ) {
							this.discountCodes.innerHTML = sectionInnerHTML.querySelector('[data-js-discount-codes]').innerHTML;
							this.discountCodes.classList.remove('processing');
							this.loadDiscountButtons();
						}
						this.input.value = '';
					})
			}

			loadDiscountButtons(){
				this.querySelectorAll('[data-js-discount]').forEach(elm=>{
					if ( ! elm.classList.contains('enabled') ) {
						elm.classList.add('enabled');
						elm.addEventListener('click', (e)=>{
							e.preventDefault();
							this.cartSubtotal.classList.add('processing');
							this.discountCodes.classList.add('processing');
							const discountToRemove = elm.dataset.jsDiscount;
							
							const allDiscountElements = this.querySelectorAll('[data-js-discount]');
							const allDiscounts = Array.from(allDiscountElements).map(el => el.dataset.jsDiscount);
							
							const remainingDiscounts = allDiscounts.filter(discount => discount !== discountToRemove);
							
							const body = JSON.stringify({ 
								discount: remainingDiscounts.join(',') 
							});
							
							fetch(`${KROWN.settings.routes.cart_update_url}`, { 
								method: 'POST',
								headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
								body 
							})
								.then(() => {
									this.handleSuccess();
								})
								.catch(error => {
									console.error(`Error: ${error}`);
								});
						});
					}
				});
			}
			
			handleError(){
				this.cartSubtotal.classList.remove('processing');
				this.discountCodes.classList.remove('processing');
				this.cartDiscountError.innerHTML = window.KROWN.settings.locales.cart_discount_code_error;
			}

		}	
	)
}